import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Upload, Search, X, Plus } from 'lucide-react';
import api from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import { apriFile } from '../../services/fileUrl';
import FirmaConsenso from './FirmaConsenso';
import SceltaFirma from './SceltaFirma';
import '../../styles/DocumentiProcedura.css';
import '../../styles/DocumentiProceduraConsole.css';
import { momentoItaliano } from '../../utils/oraItaliana';
import { messaggioDa } from '../../utils/notifiche';
import { accettaDi } from '../../utils/tipiFile';

/**
 * I due riquadri dei documenti nella schermata di una procedura.
 *
 * PERCHE' PARTONO DALL'ELENCO DEL PAZIENTE
 * I documenti si chiedono con la rotta del paziente e si filtrano per
 * procedura. Una rotta «documenti di questa visita» dovrebbe rifare da
 * capo il filtro della riservatezza estetica, e su questo gestionale una
 * regola scritta in due posti e' gia' costata una fuga di dati: il punto
 * in cui si decide chi vede cosa resta uno solo.
 */

/* Dalla fonte unica dell'ora italiana, come la schermata Documenti e
   il piede dei PDF. */
const dataOra = (v) => {
    if (!v) return '';
    const { data, ora } = momentoItaliano(v);
    return `${data} · ${ora}`;
};

const peso = (b) => (b ? `${Math.max(1, Math.round(b / 1024))} KB` : '');

/* ------------------------------------------------------------------ */
/*  Documenti generati: i consensi nati da un modello                  */
/* ------------------------------------------------------------------ */

export const DocumentiGenerati = ({ visita, onFirmato }) => {
    const [documenti, setDocumenti] = useState([]);
    const [modelli, setModelli] = useState([]);
    const [cerca, setCerca] = useState('');
    const [apertoElenco, setApertoElenco] = useState(false);
    const [daFirmare, setDaFirmare] = useState(null);
    /* DOVE FIRMA IL PAZIENTE (Alex, 6/10/2026): la stessa domanda di
       «Nuova procedura» e di «Trasforma in visita», con lo stesso
       componente. null = si chiede (SceltaFirma, che senza tablet va da
       sola al computer), 'pc' = su questo computer. Prima «Genera
       documento» firmava solo a schermo, e il consenso da firmare in testa
       alla visita - un compito della segreteria, che usa il tablet - dal
       tablet non si poteva chiudere. */
    const [dove, setDove] = useState(null);
    /* Una firma in attesa al tablet (revisione del beta, 6/10/2026, decisione
       del coordinatore): finche' c'e', «Genera documento» e' spento e dice
       perche', e la finestra ridotta a icona si riapre da qui. Prima un
       secondo modello scelto non si mandava e non lo diceva. */
    const [alTablet, setAlTablet] = useState(false);
    const [riapri, setRiapri] = useState(0);
    const [errore, setErrore] = useState(null);
    const contenitore = useRef(null);

    const pazienteId = visita?.paziente_id;
    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/modulistica/paziente/${pazienteId}`);
            const tutti = r.data?.data?.documenti || [];
            setDocumenti(tutti.filter((d) => Number(d.visita_id) === Number(visitaId)));
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere i documenti');
        }
    }, [pazienteId, visitaId]);

    useEffect(() => { carica(); }, [carica]);

    useEffect(() => {
        templateAPI.getAll()
            .then((r) => setModelli(r.data?.data?.template || []))
            .catch(() => setModelli([]));
    }, []);

    /* Cliccare fuori chiude l'elenco: un menu che resta aperto sopra il
       resto della procedura copre proprio quello che si stava guardando. */
    useEffect(() => {
        if (!apertoElenco) return undefined;
        const fuori = (e) => {
            if (contenitore.current && !contenitore.current.contains(e.target)) {
                setApertoElenco(false);
            }
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, [apertoElenco]);

    const q = cerca.trim().toLowerCase();
    const trovati = q
        ? modelli.filter((m) => `${m.titolo} ${m.categoria || ''}`.toLowerCase().includes(q))
        : modelli;

    const scegli = (m) => {
        setApertoElenco(false);
        setCerca('');
        setDove(null);
        setDaFirmare(m);
    };

    /* L'elenco da firmare e le due uscite restano gli stessi fra un
       disegno e l'altro: SceltaFirma li ha fra le dipendenze del controllo
       dello stato, e con un oggetto nuovo a ogni disegno il controllo ogni
       3 secondi ripartirebbe da capo ogni volta che la visita si ridisegna
       (un salvataggio mentre si lavora, con la firma ridotta a icona). Non
       misurato: e' il modo in cui le altre due strade gia' lo usano, con
       l'elenco nello stato. */
    const consensi = useMemo(
        () => (daFirmare ? [{ ...daFirmare, _prestazione: visita?.trattamento || null }] : []),
        [daFirmare, visita?.trattamento]
    );
    const allaFirma = useRef(onFirmato);
    allaFirma.current = onFirmato;
    const chiudiFirma = useCallback(() => { setDaFirmare(null); setDove(null); setAlTablet(false); }, []);
    const firmato = useCallback(() => {
        setDaFirmare(null);
        setDove(null);
        setAlTablet(false);
        carica();
        if (allaFirma.current) allaFirma.current();
    }, [carica]);
    /* «Passa al computer» smonta SceltaFirma prima che il suo effetto dica
       «non piu' in attesa» (revisione del beta su d6804cb): lo stato si
       rimette qui, come in chiudiFirma e firmato. */
    const alComputer = useCallback(() => { setDove('pc'); setAlTablet(false); }, []);

    return (
        <div className="dp-blocco">
            <div className="dp-azione" ref={contenitore}>
                <button type="button" className="dp-btn" disabled={alTablet}
                        onClick={() => setApertoElenco((v) => !v)}>
                    <Plus size={14} aria-hidden="true" /> Genera documento
                </button>
                {alTablet && (
                    <p className="dp-attesa-tablet" role="status">
                        C'è già un documento al tablet: aspetta la firma o annullala.
                        <button type="button" className="dp-link" onClick={() => setRiapri((n) => n + 1)}>
                            Riapri la finestra della firma
                        </button>
                    </p>
                )}

                {apertoElenco && !alTablet && (
                    <div className="dp-menu">
                        <label className="dp-cerca" data-non-trattiene>
                            <Search size={14} aria-hidden="true" />
                            <input autoFocus placeholder="Cerca un modello" value={cerca}
                                   onChange={(e) => setCerca(e.target.value)} />
                            {cerca && (
                                <button type="button" onClick={() => setCerca('')} aria-label="Togli la ricerca">
                                    <X size={13} />
                                </button>
                            )}
                        </label>
                        <div className="dp-menu-voci">
                            {modelli.length === 0 ? (
                                <p className="dp-vuoto-menu">
                                    Nessun modello di consenso disponibile. Si compongono da
                                    Documenti / Consensi.
                                </p>
                            ) : trovati.length === 0 ? (
                                <p className="dp-vuoto-menu">Nessun modello con questo nome.</p>
                            ) : trovati.map((m) => (
                                <button type="button" key={m.id} className="dp-voce"
                                        onClick={() => scegli(m)}>
                                    <b>{m.titolo}</b>
                                    <span>
                                        {m.categoria || 'senza categoria'}
                                        {m.medico_cognome ? ` · Dr. ${m.medico_cognome}` : ' · della struttura'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {errore && <p className="dp-errore">{errore}</p>}

            {documenti.length === 0 ? (
                <p className="dp-vuoto">
                    Nessun documento generato per questa procedura. Con <b>Genera
                    documento</b> si sceglie un modello, si fa firmare e resta legato
                    a questa prestazione.
                </p>
            ) : (
                <ul className="dp-elenco">
                    {documenti.map((d) => (
                        <li key={d.id}>
                            <button type="button" className="dp-file" onClick={() => apriFile(d.url)}>
                                <FileText size={15} aria-hidden="true" />
                                <span className="dp-file-nome">{d.titolo || d.tipo}</span>
                            </button>
                            <span className="dp-file-meta">
                                {d.firmato_il ? `firmato ${dataOra(d.firmato_il)}` : dataOra(d.created_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* La visita va al tablet come al computer: il documento firmato
                nasce con visita_id (sessioni_firma.visita_id, poi
                archiviaConsensoFirmato, lo stesso servizio della firma a
                schermo), e appena firmato si rilegge la visita, cosi'
                l'avviso «Consenso da firmare» sparisce allo stesso modo. */}
            {daFirmare && dove === null && (
                <SceltaFirma
                    pazienteId={pazienteId}
                    visitaId={visitaId}
                    medicoId={visita?.medico_id}
                    consensi={consensi}
                    onSuPc={alComputer}
                    onCompletato={firmato}
                    onAnnulla={chiudiFirma}
                    onInCorso={setAlTablet}
                    riapri={riapri}
                />
            )}
            {daFirmare && dove === 'pc' && (
                <FirmaConsenso
                    pazienteId={pazienteId}
                    visitaId={visitaId}
                    medicoId={visita?.medico_id}
                    consensi={consensi}
                    onClose={chiudiFirma}
                    onComplete={firmato}
                />
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Documenti caricati: i file che arrivano da fuori                   */
/* ------------------------------------------------------------------ */

/* Passano dalla stessa rotta delle fotografie di procedura, con
   `visita_id`: e' la strada che gia' applica la riservatezza estetica,
   registra l'accesso e manda il file nel cestino invece di cancellarlo.
   Una rotta nuova per i documenti avrebbe dovuto rifare tutte e tre le
   cose, e la terza si sarebbe scoperta mancante il giorno sbagliato. */
/* CHE COSA OFFRE IL SELETTORE (6/10/2026). Fino ad allora l'elenco era
   scritto qui a mano, `.pdf,.doc,.docx,.txt,.rtf,.odt,image/*`: il browser
   lasciava scegliere un Word o un testo, e il server lo rifiutava, perche' il
   tipo si legge dai byte e quei formati non li riconosce. Adesso i formati
   vengono dall'elenco chiuso del server (backend/utils/tipoFile.js, copiato
   in utils/tipiFile.js a ogni compilazione); qui si sceglie solo la famiglia:
   PDF e immagini, cioe' documenti e scansioni, come prima. I video la rotta
   li accetta, ma non sono documenti.
   SOLO I PDF (revisione del beta, 7/10/2026). Un'immagine caricata da qui
   nasceva «foto», e questo riquadro mostra i «documento»: il caricamento
   riusciva e il file non compariva. Mostrarla non si puo' (con la stessa
   procedura non si distingue da una fotografia clinica), quindi il selettore
   offre i PDF e la richiesta dichiara uso=documento, con cui il server
   rifiuta col motivo un'immagine scelta da «Tutti i file» o rinominata
   (fotoController, MSG_DOCUMENTO_SOLO_PDF). */
const ACCETTA_DOCUMENTI = accettaDi(['application/pdf']);

export const DocumentiCaricati = ({ visita }) => {
    const [file, setFile] = useState([]);
    const [caricando, setCaricando] = useState(false);
    const [errore, setErrore] = useState(null);
    const scelta = useRef(null);

    const pazienteId = visita?.paziente_id;
    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/foto/paziente/${pazienteId}`);
            const tutti = r.data?.data?.foto || [];
            setFile(tutti.filter((f) => Number(f.visita_id) === Number(visitaId)
                                     && f.categoria === 'documento'));
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere i documenti caricati');
        }
    }, [pazienteId, visitaId]);

    useEffect(() => { carica(); }, [carica]);

    const invia = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setCaricando(true);
        setErrore(null);
        try {
            const corpo = new FormData();
            corpo.append('file', f);
            corpo.append('visita_id', visitaId);
            corpo.append('uso', 'documento');
            await api.post(`/foto/paziente/${pazienteId}`, corpo,
                           { headers: { 'Content-Type': 'multipart/form-data' } });
            await carica();
        } catch (err) {
            /* Il motivo del server (il tipo letto dai byte, la dimensione).
               Arrivava gia' anche prima: l'intercettore di services/api.js
               rigetta il corpo della risposta, quindi err.message era il
               suo (misurato il 6/10/2026 col codice di prima, nella prova
               provaDocumentiSchermata). messaggioDa e' la strada di tutte le
               schermate, e sa anche il 429 con l'attesa. */
            setErrore(messaggioDa(err, 'Caricamento non riuscito'));
        } finally {
            setCaricando(false);
        }
    };

    return (
        <div className="dp-blocco">
            <div className="dp-azione">
                <button type="button" className="dp-btn" disabled={caricando}
                        onClick={() => scelta.current?.click()}>
                    <Upload size={14} aria-hidden="true" />
                    {caricando ? 'Carico…' : 'Carica documento'}
                </button>
                <input ref={scelta} type="file" hidden onChange={invia}
                       accept={ACCETTA_DOCUMENTI} />
            </div>

            {errore && <p className="dp-errore">{errore}</p>}

            {file.length === 0 ? (
                <p className="dp-vuoto">
                    Nessun documento caricato su questa procedura. Referti e
                    documenti che arrivano da fuori si aggiungono qui: seguono la
                    riservatezza della procedura come le fotografie.
                </p>
            ) : (
                <ul className="dp-elenco">
                    {file.map((f) => (
                        <li key={f.id}>
                            <button type="button" className="dp-file" onClick={() => apriFile(f.url)}>
                                <FileText size={15} aria-hidden="true" />
                                <span className="dp-file-nome">{f.etichetta || f.nome_file}</span>
                            </button>
                            <span className="dp-file-meta">
                                {peso(f.dimensione)} {dataOra(f.created_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
